/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { Component, OnInit, Optional, Injector, Inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { OKTA_CONFIG, OktaConfig, OKTA_AUTH } from '@okta/okta-angular';
import { AuthSdkError, OktaAuth } from '@okta/okta-auth-js';

import { ILogger } from '@shure/shared/angular/utils/logging';

@Component({
	selector: 'sh-okta-callback',
	template: '<div>{{ error }}</div>',
	standalone: false
})
export class OktaCallbackComponent implements OnInit {
	public error?: string;

	constructor(
		private readonly translocoService: TranslocoService,
		@Inject(OKTA_CONFIG) private config: OktaConfig,
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		@Optional() private readonly logger?: ILogger,
		@Optional() private injector?: Injector
	) {
		if (logger) {
			this.logger = logger.createScopedLogger('OktaCallbackComponent');
		}
	}

	public async ngOnInit(): Promise<void> {
		try {
			// Parse code or tokens from the URL, store tokens in the TokenManager, and redirect back to the originalUri
			await this.oktaAuth.handleLoginRedirect();
		} catch (e) {
			// Callback from social IDP. Show custom login page to continue.
			const isInteractionRequiredError =
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore Supports auth-js v5 & v6-7
				this.oktaAuth.isInteractionRequiredError || this.oktaAuth.idx.isInteractionRequiredError;
			if (isInteractionRequiredError(e) && this.injector) {
				const { onAuthResume, onAuthRequired } = this.config;
				const callbackFn = onAuthResume || onAuthRequired;
				if (callbackFn) {
					callbackFn(this.oktaAuth, this.injector);
					return;
				}
			}
			if (e instanceof AuthSdkError) {
				this.error =
					e.message ===
					'Could not load PKCE codeVerifier from storage. This may indicate the auth flow has already completed or multiple auth flows are executing concurrently.'
						? this.translocoService.translate('cloud.shared.error-labels.okta-back-navigation')
						: e.toString();
				if (this.logger) {
					this.logger.error('handleError', e.toString());
				}
			} else {
				this.error = (e as Error).toString();
			}
		}
	}
}
